'use client';

import type { VectorDiagramBlock } from '@jeraisonne/ai/visual-blocks';
import { makePlotView } from './plot/projection';
import { niceStep, ticks } from './plot/grid';

const DEFAULT_COLORS = ['#3b82f6', '#ef4444', '#22c55e', '#f59e0b', '#8b5cf6'];

const VB_W = 360;
const VB_H = 300;
const VP_PAD = 28;

function ArrowDefs({ blockId }: { blockId: string }) {
  return (
    <defs>
      {DEFAULT_COLORS.map((color, i) => (
        <marker
          key={i}
          id={`arrow-${blockId}-${i}`}
          markerWidth="10"
          markerHeight="7"
          refX="10"
          refY="3.5"
          orient="auto"
        >
          <polygon points="0 0, 10 3.5, 0 7" fill={color} />
        </marker>
      ))}
    </defs>
  );
}

/**
 * Rendu SVG pur d'un diagramme de vecteurs AVEC viewport (remplace JSXGraph).
 * Projection isotrope (angles préservés), grille/axes optionnels.
 */
function VectorDiagramViewport({ block }: { block: VectorDiagramBlock }) {
  const vp = block.viewport!;
  const view = makePlotView(vp, { w: VB_W, h: VB_H }, { isotropic: true, pad: VP_PAD });
  const showGrid = block.grid ?? false;
  const showAxis = block.axis ?? true;
  const stepX = niceStep(vp.xMax - vp.xMin);
  const stepY = niceStep(vp.yMax - vp.yMin);
  const markerOf = (color: string, i: number) => {
    const idx = DEFAULT_COLORS.indexOf(color);
    return `arrow-${block.id}-${idx >= 0 ? idx : i % DEFAULT_COLORS.length}`;
  };

  return (
    <div className="w-full overflow-x-auto">
      <svg
        viewBox={`0 0 ${VB_W} ${VB_H}`}
        className="mx-auto w-full max-w-lg"
        style={{ maxHeight: '320px' }}
        role="img"
        aria-label={block.caption ?? block.title ?? 'Diagramme de vecteurs'}
      >
        <ArrowDefs blockId={block.id} />

        {showGrid &&
          ticks(vp.xMin, vp.xMax, stepX).map((x) => (
            <line
              key={`gx-${x}`}
              x1={view.tx(x)}
              y1={view.ty(vp.yMax)}
              x2={view.tx(x)}
              y2={view.ty(vp.yMin)}
              stroke="#eef2f7"
              strokeWidth={1}
            />
          ))}
        {showGrid &&
          ticks(vp.yMin, vp.yMax, stepY).map((y) => (
            <line
              key={`gy-${y}`}
              x1={view.tx(vp.xMin)}
              y1={view.ty(y)}
              x2={view.tx(vp.xMax)}
              y2={view.ty(y)}
              stroke="#eef2f7"
              strokeWidth={1}
            />
          ))}

        {showAxis && vp.yMin <= 0 && vp.yMax >= 0 && (
          <line
            x1={view.tx(vp.xMin)}
            y1={view.ty(0)}
            x2={view.tx(vp.xMax)}
            y2={view.ty(0)}
            stroke="#94a3b8"
            strokeWidth={1.5}
          />
        )}
        {showAxis && vp.xMin <= 0 && vp.xMax >= 0 && (
          <line
            x1={view.tx(0)}
            y1={view.ty(vp.yMin)}
            x2={view.tx(0)}
            y2={view.ty(vp.yMax)}
            stroke="#94a3b8"
            strokeWidth={1.5}
          />
        )}

        {block.vectors.map((v, i) => {
          const color = v.color ?? DEFAULT_COLORS[i % DEFAULT_COLORS.length]!;
          const x1 = view.tx(v.from[0]);
          const y1 = view.ty(v.from[1]);
          const x2 = view.tx(v.to[0]);
          const y2 = view.ty(v.to[1]);
          return (
            <g key={v.id}>
              <line
                x1={x1}
                y1={y1}
                x2={x2}
                y2={y2}
                stroke={color}
                strokeWidth={2}
                markerEnd={`url(#${markerOf(color, i)})`}
              />
              {(v.label ?? v.name) && (
                <text
                  x={(x1 + x2) / 2 + 6}
                  y={(y1 + y2) / 2 - 6}
                  fontSize={12}
                  fontWeight={600}
                  fill={color}
                >
                  {v.label ?? v.name}
                </text>
              )}
            </g>
          );
        })}

        {(block.points ?? []).map((pt) => (
          <g key={pt.id}>
            <circle cx={view.tx(pt.x)} cy={view.ty(pt.y)} r={4} fill={pt.color ?? '#374151'} />
            {pt.name && (
              <text
                x={view.tx(pt.x) + 8}
                y={view.ty(pt.y) - 8}
                fontSize={11}
                fill={pt.color ?? '#374151'}
              >
                {pt.name}
              </text>
            )}
          </g>
        ))}
      </svg>
    </div>
  );
}

const PADDING = 40;

/** Rendu SVG auto-cadré (sans viewport) — inchangé. */
function VectorDiagramSVG({ block }: { block: VectorDiagramBlock }) {
  const allXs = block.vectors.flatMap((v) => [v.from[0], v.to[0]]);
  const allYs = block.vectors.flatMap((v) => [v.from[1], v.to[1]]);
  (block.points ?? []).forEach((p) => {
    allXs.push(p.x);
    allYs.push(p.y);
  });

  const minX = Math.min(...allXs, 0) - PADDING;
  const maxX = Math.max(...allXs, 0) + PADDING;
  const minY = Math.min(...allYs, 0) - PADDING;
  const maxY = Math.max(...allYs, 0) + PADDING;
  const w = maxX - minX;
  const h = maxY - minY;

  function tx(x: number) {
    return x - minX;
  }
  function ty(y: number) {
    return h - (y - minY);
  }

  return (
    <div className="w-full overflow-x-auto">
      <svg
        viewBox={`0 0 ${w} ${h}`}
        className="mx-auto w-full max-w-lg"
        style={{ maxHeight: '300px' }}
        role="img"
        aria-label={block.caption ?? block.title ?? 'Diagramme de vecteurs'}
      >
        <ArrowDefs blockId={block.id} />

        {/* Draw vectors */}
        {block.vectors.map((v, i) => {
          const color = v.color ?? DEFAULT_COLORS[i % DEFAULT_COLORS.length]!;
          const colorIdx = DEFAULT_COLORS.indexOf(color);
          const markerId = `arrow-${block.id}-${colorIdx >= 0 ? colorIdx : i}`;
          const x1 = tx(v.from[0]);
          const y1 = ty(v.from[1]);
          const x2 = tx(v.to[0]);
          const y2 = ty(v.to[1]);
          return (
            <g key={v.id}>
              <line
                x1={x1}
                y1={y1}
                x2={x2}
                y2={y2}
                stroke={color}
                strokeWidth={2}
                markerEnd={`url(#${markerId})`}
              />
              {(v.label ?? v.name) && (
                <text
                  x={(x1 + x2) / 2 + 6}
                  y={(y1 + y2) / 2 - 6}
                  fontSize={13}
                  fontWeight={600}
                  fill={color}
                >
                  {v.label ?? v.name}
                </text>
              )}
            </g>
          );
        })}

        {/* Draw named points */}
        {(block.points ?? []).map((pt) => (
          <g key={pt.id}>
            <circle cx={tx(pt.x)} cy={ty(pt.y)} r={4} fill={pt.color ?? '#374151'} />
            {pt.name && (
              <text x={tx(pt.x) + 8} y={ty(pt.y) - 8} fontSize={12} fill={pt.color ?? '#374151'}>
                {pt.name}
              </text>
            )}
          </g>
        ))}
      </svg>
    </div>
  );
}

export function VectorDiagramBlockRenderer({ block }: { block: VectorDiagramBlock }) {
  if (block.viewport) {
    return <VectorDiagramViewport block={block} />;
  }
  return <VectorDiagramSVG block={block} />;
}
